import React, { useEffect } from "react";
import { useQuery, useQueryClient  } from "@tanstack/react-query";
import CookieManager from "utilities/js/localstorage/CookieManager";
import Constants from "utilities/js/Constants";
import {getUserInfo} from "utilities/js/callouts/CalloutFactory";
import './AccountSettingsTabs.css';
import AccountSettingsTab_General from "./AccountSettingsTab_General";
import AccountSettingsTab_Social from "./AccountSettingsTab_Social";
import AccountSettingsTab_Bracket from "./AccountSettingsTab_Bracket";
import { updateAccount } from "utilities/js/callouts/CalloutFactory";
import Toast from "components/reusable/Toast";
import { showErrorToast } from "utilities/js/ToastHelper";
import { getAccountColors } from "utilities/js/callouts/CalloutFactory";

const loadingIcon = Constants.ICON_LOADING;

function AccountSettingsTabs(props) {

    const queryClient = useQueryClient();

    //Callouts usings React Query cache
    const allowableColors = useAccountColors();
    const userInfo = useUserInfo();

    //State
    const [updatedUserInfo, setUpdatedUserInfo] = React.useState();
    const [selectedSettingsPage, setSelectedSettingsPage] = React.useState('General');
    const [isTabular, setIsTabular] = React.useState(true);
    const [mode, setMode] = React.useState('view');
    const [toastMessages, setToastMessages] = React.useState([]);
    
    const cookieManager = new CookieManager();

    //DOM References
    const accountInfoNavRef = React.createRef();

    //Initialize screen
    useEffect(setupScreenSizeListener, []);


    useEffect(() => {
        //If userInfo changes, we set the updatedUserInfo
        //userInfo should only change when we manually invalidate our cache
        if(userInfo) {
            setUpdatedUserInfo({...userInfo});
        }
    }, [userInfo]);


    function useUserInfo(){
        const {status, data, error} = useQuery({
            queryKey: ['userInfo'], 
            queryFn: () => getUserInfo(cookieManager.getValue(Constants.COOKIE_USER_EMAIL_KEY)), 
            staleTime: Infinity
        });
        if(status === 'success'){
            return data;
        }else if(status === 'error'){
            if(error.code){
                console.log(error.code);
                cookieManager.clearAllValues();          
                window.location.reload();
            }
        }
    }

    function useAccountColors(){
        const {status, data, error, isFetching} = useQuery({
            queryKey: ['accountColors'], 
            queryFn: getAccountColors, 
            staleTime: Infinity
        });
        if(status === 'success'){
            return data;
        }else if(status === 'error'){
            if(error.code){
                console.log(error.code);
            }
        }
    }

    function setupScreenSizeListener(){
        const mediaQuery = window.matchMedia('(max-width: 700px)');

        // Register event listener
        mediaQuery.addEventListener("change", handleSizeChange);
    
        // Initial check
        handleSizeChange(mediaQuery);
    }

    function handleSizeChange(e){
        // Check if the media query is true
        if (e.matches) {
            setIsTabular(false);
            console.log('Media Query Matched!');
        }else{
            setIsTabular(true);
        }
    }

    function loadingScreen(){
        if(!userInfo){
            return (<div className="loading-icon-container"><img src={loadingIcon} alt="loading"></img></div>)
        }
        return;
    }

    function getAppropriateSettingsPage(){
        if(isTabular){
            let settingsPageOptions = getSettingsPageOptions();
            if(settingsPageOptions[selectedSettingsPage]){
                return settingsPageOptions[selectedSettingsPage];
            }
        }else{
            let settingsPageOptions = getSettingsPageOptions();
            return Object.values(settingsPageOptions);
        }
    }

    function toggleSelectedSettingsPage(event){
        let selectedTab = event.currentTarget.dataset.name;
        let navTabs = accountInfoNavRef.current.querySelectorAll('.account-info-nav-tab');
        navTabs.forEach((tab) => {
            if(tab.className.includes(' selected')){
                tab.className = tab.className.replace(' selected', '');
            }
      
            if(tab.dataset.name === selectedTab){
                tab.className += ' selected';
            }
          })
          setSelectedSettingsPage(selectedTab);

    }

    function getSettingsPageOptions(){
        return {
            'General': (<AccountSettingsTab_General userInfo={updatedUserInfo} mode={mode} updateSetting={setSetting} allowableColors={allowableColors}></AccountSettingsTab_General>),
            'Bracket':(<AccountSettingsTab_Bracket userInfo={updatedUserInfo} mode={mode} updateSetting={setSetting}></AccountSettingsTab_Bracket>),
            'Social':(<AccountSettingsTab_Social userInfo={updatedUserInfo} mode={mode} updateSetting={setSetting}></AccountSettingsTab_Social>)
        };
    }

    function setSetting(settings){
        let userInfo = updatedUserInfo;
        userInfo = Object.assign(userInfo, settings);
        //Need to create a new object here for the child components to rerender
        setUpdatedUserInfo({...userInfo});
    }

    function setEditMode(){
        setMode('edit');
    }

    function setViewMode(){
        setMode('view');
        setUpdatedUserInfo(JSON.parse(JSON.stringify(userInfo)));
    }

    function saveSettings(){
        updateAccount(updatedUserInfo).then(response => {
            //Next time the page changes, make a new callout to set UserInfo
            queryClient.invalidateQueries({ queryKey: ['userInfo'] })
            setUpdatedUserInfo(response);
            setMode('view');
        }).catch(error => {
            showErrorToast(this, 'Your request cannot be processed at this time');
            console.log('unable to complete request');
        })
    }

    function editControls(){
        if(mode === 'view'){
            return (
                <div className="settings-edit-controls">
                    <button className="nav-button" onClick={setEditMode}>Edit Settings</button>
                </div>
            )

        }
    }

    function saveChangesControls(){
        if(mode === 'edit'){
            return (
                <div className="settings-save-controls">
                    <button className="nav-button nav-button-green" onClick={saveSettings}>Save</button>
                    <button className="nav-button" onClick={setViewMode}>Cancel</button>
                </div>
            )
        }
    }

    function getAccountInfoScreen(){
        if(updatedUserInfo){
            return (
                <>
                <div className="vertical-centering-container">
                    <div className="account-info-container">
                        <div className="account-info-nav hide-mobile" ref={accountInfoNavRef}>
                            <div className="account-info-nav-tab selected" data-name="General" onClick={toggleSelectedSettingsPage}>General</div>
                            <div className="account-info-nav-tab" data-name="Bracket" onClick={toggleSelectedSettingsPage}>Bracket</div>
                            <div className="account-info-nav-tab" data-name="Social" onClick={toggleSelectedSettingsPage}>Social</div>
                        </div>

                        <div className="account-info-content">
                            {getAppropriateSettingsPage()}
                        </div>
                    </div>
                    {saveChangesControls()}
                    {editControls()}
                </div>
                
                </>
            ); 
        }
    }

    


    return (
        <>
            {loadingScreen()}
            {getAccountInfoScreen()}     
            <Toast toastList={toastMessages} position="top-left" autoDelete="true" autoDeleteTime="5000"/> 
        </>
    );
    
}

















// const cookieManager = new CookieManager();

// class AccountSettingsTabs extends React.Component {

//     constructor(props){
//         super(props);
        
//         this.accountInfoNavRef = React.createRef();
//         this.state = {
//             isLoading: true,
//             userInfo: {},
//             // firstName: '',
//             // lastName: '',
//             // dateJoined: '',
//             // email: '',
//             // isPremium: '',
//             // isEmailVerified: '',
//             selectedSettingsPage: 'General',
//             isTabular: true,
//             mode: 'view',
//             updatedUserInfo: {},
//             toastMessages: []
//         };

//         this.closePanel = this.closePanel.bind(this);
//         this.loadingScreen = this.loadingScreen.bind(this);
//         this.getAccountInfoScreen = this.getAccountInfoScreen.bind(this);
//         this.setSelectedSettingsPage = this.setSelectedSettingsPage.bind(this);
//         this.getAppropriateSettingsPage = this.getAppropriateSettingsPage.bind(this);
//         this.getSettingsPageOptions = this.getSettingsPageOptions.bind(this);
//         this.handleSizeChange = this.handleSizeChange.bind(this);
//         this.setEditMode = this.setEditMode.bind(this);
//         this.setViewMode = this.setViewMode.bind(this);
//         this.setSetting = this.setSetting.bind(this);
//         this.saveSettings = this.saveSettings.bind(this);
//     }

//     // getAllowableColors(){
//     //     const {status, data, error, isFetching} = useQuery({queryKey: ['accountColors'], queryFn: getAccountColors, staleTime: 1000})

                            
//     //     if(status === 'success'){
//     //         this.setState({
//     //             allowableColors: data
//     //         });
//     //         this.setState({isLoading: false});
//     //     }else if(status === 'error'){
//     //         if(error.code){
//     //             console.log(error.code);
//     //             cookieManager.clearAllValues();          
//     //             window.location.reload();
//     //         }
//     //     }
                        
//     //     getAccountColors().then(response => {
//     //         this.setState({
//     //             allowableColors: response,
//     //         })
//     //         this.setSelectedColorOnLoad();
//     //     }).catch(error => {
//     //         console.log('cound not retrieve colors');
//     //         console.log(error);
//     //         this.setState({disableChangeColorButton: true})

//     //     })
        
//     // }
//     async componentDidMount(){

//         try{
        
//             let response = await getUserInfo(cookieManager.getValue(Constants.COOKIE_USER_EMAIL_KEY))
//             // let colors = await getAccountColors();
//             this.setState({
//                 userInfo: JSON.parse(JSON.stringify(response)),
//                 updatedUserInfo: JSON.parse(JSON.stringify(response)),
//                 // firstName: response.first_name ? response.first_name : '',
//                 // lastName: response.last_name ? response.last_name : '',
//                 // email: response.email ? response.email : '',
//                 // isPremium: response.premium ? response.premium : '',
//                 // dateJoined: response.created_date ? new Date(parseInt(response.created_date)).toLocaleDateString('en-us', { weekday:"long", year:"numeric", month:"short", day:"numeric"}) : '',
//                 // isEmailVerified: response.email_verified ? response.email_verified : '',
//                 // allowableColors: colors
//             });
//             this.setState({isLoading: false});
//         }catch(error){
//             if(error.code){
//                 console.log(error.code);
//                 cookieManager.clearAllValues();          
//                 window.location.reload();
//             }
//         }
//         this.setupScreenSizeListener();
//     }

//     setupScreenSizeListener(){
//         const mediaQuery = window.matchMedia('(max-width: 700px)');

//         // Register event listener
//         mediaQuery.addEventListener("change", this.handleSizeChange);
    
//         // Initial check
//         this.handleSizeChange(mediaQuery);
//     }

//     handleSizeChange(e){
//         // Check if the media query is true
//         if (e.matches) {
//             this.setState({isTabular: false});
//             console.log('Media Query Matched!');
//         }else{
//             this.setState({isTabular: true});
//         }
//     }

//     closePanel(){
//         this.props.closePanel();
//     }

//     loadingScreen(){
//         if(this.state.isLoading){
//             return (<div className="loading-icon-container"><img src={loadingIcon} alt="loading"></img></div>)
//         }
//         return;
//     }

//     getAppropriateSettingsPage(){
//         if(this.state.isTabular){
//             let settingsPageOptions = this.getSettingsPageOptions();
//             if(settingsPageOptions[this.state.selectedSettingsPage]){
//                 return settingsPageOptions[this.state.selectedSettingsPage];
//             }
//         }else{
//             let settingsPageOptions = this.getSettingsPageOptions();
//             return Object.values(settingsPageOptions);

//         }
//     }

//     setSelectedSettingsPage(event){
//         let selectedTab = event.currentTarget.dataset.name;
//         let navTabs = this.accountInfoNavRef.current.querySelectorAll('.account-info-nav-tab');
//         navTabs.forEach((tab) => {
//             if(tab.className.includes(' selected')){
//                 tab.className = tab.className.replace(' selected', '');
//             }
      
//             if(tab.dataset.name === selectedTab){
//                 tab.className += ' selected';
//             }
//           })
//           this.setState({selectedSettingsPage: selectedTab});

//     }

//     getSettingsPageOptions(){
//         return {
//             'General': (<AccountSettingsTab_General userInfo={this.state.updatedUserInfo} mode={this.state.mode} updateSetting={this.setSetting} allowableColors={this.state.allowableColors}></AccountSettingsTab_General>),
//             'Bracket':(<AccountSettingsTab_Bracket userInfo={this.state.updatedUserInfo} mode={this.state.mode} updateSetting={this.setSetting}></AccountSettingsTab_Bracket>),
//             'Social':(<AccountSettingsTab_Social userInfo={this.state.updatedUserInfo} mode={this.state.mode} updateSetting={this.setSetting}></AccountSettingsTab_Social>)
//         };
//     }

//     setSetting(settings){
//         let userInfo = this.state.updatedUserInfo;
//         userInfo = Object.assign(userInfo, settings);
//         this.setState({updatedUserInfo: userInfo});
//     }

//     setEditMode(){
//         this.setState({mode: 'edit'});
//     }

//     setViewMode(){
//         this.setState({mode: 'view', updatedUserInfo: JSON.parse(JSON.stringify(this.state.userInfo))})
//     }

//     saveSettings(){
//             this.setState({disabledPrivateButton: true})
//             updateAccount(this.state.updatedUserInfo).then(response => {
//                 this.setState({userInfo: response, updatedUserInfo: response, mode: 'view'});
//             }).catch(error => {
//                 showErrorToast(this, 'Your request cannot be processed at this time');
//                 console.log('unable to complete request');
//             }).finally(() => {
//                 setTimeout(() => {
//                     this.setState({disabledPrivateButton: false});
//                 }, 1000)
//             })
//     }

//     get editControls(){
//         if(this.state.mode === 'view'){
//             return (
//                 <div className="settings-edit-controls">
//                     <button className="nav-button" onClick={this.setEditMode}>Edit Settings</button>
//                 </div>
//             )

//         }
//     }

//     get saveChangesControls(){
//         if(this.state.mode === 'edit'){
//             return (
//                 <div className="settings-save-controls">
//                     <button className="nav-button nav-button-green" onClick={this.saveSettings}>Save</button>
//                     <button className="nav-button" onClick={this.setViewMode}>Cancel</button>
//                 </div>
//             )
//         }
//     }

//     getAccountInfoScreen(){
//         if(!this.state.isLoading){
//             return (
//                 <>
//                 <div className="vertical-centering-container">
//                     <div className="account-info-container">
//                         <div className="account-info-nav hide-mobile" ref={this.accountInfoNavRef}>
//                             <div className="account-info-nav-tab selected" data-name="General" onClick={this.setSelectedSettingsPage}>General</div>
//                             <div className="account-info-nav-tab" data-name="Bracket" onClick={this.setSelectedSettingsPage}>Bracket</div>
//                             <div className="account-info-nav-tab" data-name="Social" onClick={this.setSelectedSettingsPage}>Social</div>
//                         </div>

//                         <div className="account-info-content">
//                             {this.getAppropriateSettingsPage()}
//                         </div>
//                     </div>
//                     {this.saveChangesControls}
//                     {this.editControls}
//                 </div>
                
//                 </>
//             ); 
//         }
//     }

//     render(){

//         return (
//             <>
//                 {this.loadingScreen()}
//                 {this.getAccountInfoScreen()}     
//                 <Toast toastList={this.state.toastMessages} position="top-left" autoDelete="true" autoDeleteTime="5000"/> 
//                 {/* <UseQuery queryKey={['accountColors']} queryFn={getAccountColors} options={{staleTime: 1000}}>
//                     {
//                         (status, data, error, isFetching) => {
                            
//                             if(status === 'success'){
//                                 this.setState({
//                                     allowableColors: data
//                                 });
//                                 this.setState({isLoading: false});
//                             }else if(status === 'error'){
//                                 if(error.code){
//                                     console.log(error.code);
//                                     cookieManager.clearAllValues();          
//                                     window.location.reload();
//                                 }
//                             }
//                         }
//                     }
//                 </UseQuery>                         */}
//             </>
//         );
//     }
// }

export default AccountSettingsTabs;
